import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Alert, Typography, Grid, Paper, Tabs, Tab,
  Link, Switch, FormControlLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { DocumentPanel } from '../../components/documents/DocumentPanel';
import { VehicleDocumentChecklist } from '../../components/documents/VehicleDocumentChecklist';
import { vehicleAPI, driverAPI, helperAPI } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { brandColors, RADIUS, brandCss, BASE_FONT_SIZE } from '../../theme/appTheme';
import {
  masterVehicleName,
  ownershipLabel,
  resolveVehicleTypePreset,
  vehicleTypeIcon,
} from '../../config/vehicleTypePresets';

type ProfileType = 'vehicles' | 'drivers' | 'helpers';

type DetailField = {
  key: string;
  label: string;
  section?: string;
  showWhen?: (record: Record<string, unknown>) => boolean;
  format?: 'date' | 'file' | 'status' | 'owner' | 'currency' | 'percent' | 'payment';
  fullWidth?: boolean;
};

const isLeased = (r: Record<string, unknown>) => ['leased', 'rented'].includes(String(r.ownerType || ''));

const profileConfig: Record<ProfileType, {
  entityType: 'vehicle' | 'driver' | 'helper';
  titleField: string;
  subtitleField: string;
  icon: React.ReactNode;
  listPath: string;
  listLabel: string;
  entityLabel: string;
  fields: DetailField[];
  tabs: string[];
}> = {
  vehicles: {
    entityType: 'vehicle',
    titleField: 'vehicleNumber',
    subtitleField: 'vehicleModel',
    icon: <DirectionsCarIcon sx={{ fontSize: 32 }} />,
    listPath: '/vehicles',
    listLabel: 'Vehicles',
    entityLabel: 'Vehicle',
    tabs: ['Details', 'Documents'],
    fields: [
      { key: 'vehicleNumber', label: 'Vehicle Number', section: 'General Information' },
      { key: 'vehicleType', label: 'Vehicle Type', section: 'General Information' },
      { key: 'brand', label: 'Make', section: 'General Information' },
      { key: 'vehicleModel', label: 'Model', section: 'General Information' },
      { key: 'year', label: 'Year', section: 'General Information' },
      { key: 'capacity', label: 'Capacity (kg)', section: 'General Information' },
      { key: 'fuelType', label: 'Fuel Type', section: 'General Information' },
      { key: 'maxSpeed', label: 'Max Speed (km/h)', section: 'General Information' },
      { key: 'clientName', label: 'Client', section: 'General Information' },
      { key: 'size', label: 'Size', section: 'General Information' },
      { key: 'ownerType', label: 'Ownership', section: 'Ownership', format: 'owner' },
      { key: 'status', label: 'Active or Inactive', section: 'Ownership', format: 'status' },
      { key: 'vendorName', label: 'Vendor', section: 'Vendor Lease', showWhen: isLeased },
      { key: 'vendorStartDate', label: 'Start Date', section: 'Vendor Lease', format: 'date', showWhen: isLeased },
      { key: 'vendorEndDate', label: 'End Date', section: 'Vendor Lease', format: 'date', showWhen: isLeased },
    ],
  },
  drivers: {
    entityType: 'driver',
    titleField: 'driverName',
    subtitleField: 'driverCode',
    icon: <PersonIcon sx={{ fontSize: 32 }} />,
    listPath: '/drivers',
    listLabel: 'Drivers',
    entityLabel: 'Driver',
    tabs: ['Details', 'Documents'],
    fields: [
      { key: 'driverCode', label: 'Driver Code', section: 'Driver Information' },
      { key: 'driverName', label: 'Name', section: 'Driver Information' },
      { key: 'mobile', label: 'Mobile Number', section: 'Driver Information' },
      { key: 'email', label: 'Email Address', section: 'Driver Information' },
      { key: 'aadhaarNumber', label: 'Aadhaar Number', section: 'Driver Information' },
      { key: 'dateOfBirth', label: 'Date of Birth', section: 'Driver Information', format: 'date' },
      { key: 'address', label: 'Address', section: 'Driver Information', fullWidth: true },
      { key: 'licenseNumber', label: 'License Number', section: 'Driver Information' },
      { key: 'licenseExpiry', label: 'License Expiry', section: 'Driver Information', format: 'date' },
      { key: 'reference1Name', label: 'Reference 1 Name', section: 'Driver Information' },
      { key: 'reference1Mobile', label: 'Reference 1 Mobile', section: 'Driver Information' },
      { key: 'reference2Name', label: 'Reference 2 Name', section: 'Driver Information' },
      { key: 'reference2Mobile', label: 'Reference 2 Mobile', section: 'Driver Information' },
      { key: 'joiningDate', label: 'Joining Date', section: 'Driver Information', format: 'date' },
      { key: 'status', label: 'Active / Inactive', section: 'Driver Information', format: 'status' },
      { key: 'paymentType', label: 'Payment Type', section: 'Payment', format: 'payment' },
      {
        key: 'monthlySalary', label: 'Monthly Salary', section: 'Payment', format: 'currency',
        showWhen: (r) => String(r.paymentType || '') === 'monthly_salary',
      },
      {
        key: 'dailyWage', label: 'Daily Wage', section: 'Payment', format: 'currency',
        showWhen: (r) => String(r.paymentType || '') === 'daily_wage',
      },
      {
        key: 'tripCommission', label: 'Trip Commission', section: 'Payment', format: 'percent',
        showWhen: (r) => String(r.paymentType || '') === 'trip_commission',
      },
    ],
  },
  helpers: {
    entityType: 'helper',
    titleField: 'helperName',
    subtitleField: 'helperCode',
    icon: <GroupsIcon sx={{ fontSize: 32 }} />,
    listPath: '/helpers',
    listLabel: 'Helpers',
    entityLabel: 'Helper',
    tabs: ['Details', 'Documents'],
    fields: [
      { key: 'helperCode', label: 'Helper Code', section: 'Helper Information' },
      { key: 'helperName', label: 'Name', section: 'Helper Information' },
      { key: 'mobile', label: 'Mobile', section: 'Helper Information' },
      { key: 'fatherName', label: 'Father Name', section: 'Helper Information' },
      { key: 'address', label: 'Address', section: 'Helper Information', fullWidth: true },
      { key: 'reference1Name', label: 'Reference 1 Name', section: 'Helper Information' },
      { key: 'reference1Mobile', label: 'Reference 1 Mobile', section: 'Helper Information' },
      { key: 'reference2Name', label: 'Reference 2 Name', section: 'Helper Information' },
      { key: 'reference2Mobile', label: 'Reference 2 Mobile', section: 'Helper Information' },
      { key: 'status', label: 'Status', section: 'Helper Information', format: 'status' },
    ],
  },
};

const statusMeta = (status: string) => {
  const s = status.toLowerCase();
  if (s === 'active') return { label: 'Active', bg: '#dcfce7', color: '#166534' };
  if (s === 'inactive') return { label: 'Inactive', bg: '#fee2e2', color: '#991b1b' };
  if (s === 'maintenance') return { label: 'In service', bg: '#fef3c7', color: '#b45309' };
  return { label: status || '—', bg: '#f1f5f9', color: '#475569' };
};

const paymentLabel = (v: string) => {
  if (v === 'daily_wage') return 'Daily Wage';
  if (v === 'trip_commission') return 'Trip Commission';
  return 'Monthly Salary';
};

const isImageUrl = (url: string) => /\.(jpe?g|png|gif|webp)(\?|$)/i.test(url);

const FieldCard: React.FC<{ label: string; children: React.ReactNode; fullWidth?: boolean }> = ({
  label, children, fullWidth,
}) => (
  <Grid item xs={12} sm={fullWidth ? 12 : 6} md={fullWidth ? 12 : 4}>
    <Box sx={{
      p: 1.75,
      height: '100%',
      borderRadius: '12px',
      bgcolor: '#f8fafc',
      border: `1px solid ${brandColors.border}`,
      transition: 'border-color 0.15s, box-shadow 0.15s',
      '&:hover': {
        borderColor: `${brandCss.primary}55`,
        boxShadow: `0 4px 14px ${brandCss.primary}12`,
      },
    }}>
      <Typography sx={{
        fontSize: 11,
        color: brandColors.textMuted,
        fontWeight: 700,
        mb: 0.65,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
      }}>
        {label}
      </Typography>
      <Box sx={{ fontSize: BASE_FONT_SIZE, fontWeight: 700, color: brandColors.textDark, wordBreak: 'break-word', lineHeight: 1.35 }}>
        {children}
      </Box>
    </Box>
  </Grid>
);

const MetaRow: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
    <Box sx={{ color: brandCss.primary, mt: 0.15, flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 11, color: brandColors.textMuted, fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: BASE_FONT_SIZE, fontWeight: 700, color: brandColors.textDark, wordBreak: 'break-word' }}>
        {value || '—'}
      </Typography>
    </Box>
  </Box>
);

export const EntityProfilePage: React.FC<{ profileType: ProfileType }> = ({ profileType }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { formatDate, formatCurrency } = useLocale();
  const config = profileConfig[profileType];
  const [tab, setTab] = useState(0);
  const [record, setRecord] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const api = profileType === 'vehicles'
          ? vehicleAPI
          : profileType === 'helpers'
            ? helperAPI
            : driverAPI;
        const res = await api.getById(id);
        setRecord(res.data);
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        setError(msg || 'Failed to load record');
        setRecord(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, profileType]);

  const visibleFields = useMemo(() => {
    if (!record) return [];
    return config.fields.filter((f) => !f.showWhen || f.showWhen(record));
  }, [config.fields, record]);

  const groups = useMemo(() => {
    const result: { title: string; fields: DetailField[] }[] = [];
    visibleFields.forEach((field) => {
      const section = field.section || 'Details';
      const last = result[result.length - 1];
      if (last && last.title === section) last.fields.push(field);
      else result.push({ title: section, fields: [field] });
    });
    return result;
  }, [visibleFields]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress sx={{ color: brandCss.primary }} />
      </Box>
    );
  }

  if (error || !record || !id) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Record not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(config.listPath)}>
          Back to {config.listLabel}
        </Button>
      </Box>
    );
  }

  const title = String(record[config.titleField] || 'Profile');
  const subtitle = profileType === 'vehicles'
    ? [
      masterVehicleName(record, ['brand', 'makeName']),
      masterVehicleName(record, ['vehicleModel', 'modelName']),
      record.year ? String(record.year) : '',
    ].filter((v) => v && v !== '—').join(' · ')
    : String(record[config.subtitleField] || '');
  const status = statusMeta(String(record.status || ''));
  const photo = String(record.photo || '');
  const isActive = String(record.status || '').toLowerCase() === 'active';

  const formatValue = (field: DetailField, value: unknown): React.ReactNode => {
    if (field.key === 'brand') {
      return masterVehicleName(record, ['brand', 'makeName']);
    }
    if (field.key === 'vehicleModel') {
      return masterVehicleName(record, ['vehicleModel', 'modelName']);
    }
    if (field.key === 'vehicleType') {
      const name = masterVehicleName(record, ['vehicleType', 'typeName']);
      if (name === '—') return '—';
      const preset = resolveVehicleTypePreset(name);
      return (
        <Chip
          size="small"
          icon={<Box sx={{ display: 'flex', color: preset.color, ml: 0.5 }}>{vehicleTypeIcon(preset, 14)}</Box>}
          label={name}
          sx={{
            bgcolor: preset.bg,
            color: preset.color,
            fontWeight: 700,
            height: 26,
            '& .MuiChip-icon': { color: preset.color },
          }}
        />
      );
    }
    if (field.key === 'capacity') {
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) return '—';
      return `${n.toLocaleString('en-IN')} kg`;
    }
    if (field.key === 'maxSpeed') {
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) return '—';
      return `${n} km/h`;
    }
    if (field.format === 'owner') {
      return ownershipLabel(record);
    }
    if (field.format === 'payment') return paymentLabel(String(value || ''));
    if (field.format === 'currency') {
      const n = Number(value || 0);
      return formatCurrency ? formatCurrency(n) : `₹ ${n.toLocaleString('en-IN')}`;
    }
    if (field.format === 'percent') {
      const n = Number(value || 0);
      return `${n}%`;
    }
    if (field.format === 'status') {
      const meta = statusMeta(String(value || ''));
      return (
        <Chip label={meta.label} size="small" sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700, height: 24 }} />
      );
    }
    if (field.format === 'file') {
      const url = String(value || '');
      if (!url) return '—';
      if (isImageUrl(url)) {
        return (
          <Box component="img" src={url} alt={field.label}
            sx={{ maxWidth: '100%', maxHeight: 120, borderRadius: 1.5, border: `1px solid ${brandColors.border}` }} />
        );
      }
      return (
        <Link href={url} target="_blank" rel="noopener noreferrer"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 700, fontSize: BASE_FONT_SIZE }}>
          <InsertDriveFileOutlinedIcon sx={{ fontSize: 18 }} />
          View document
        </Link>
      );
    }
    if (value == null || value === '') return '—';
    if (field.format === 'date' || field.key.toLowerCase().includes('expiry') || field.key.toLowerCase().includes('date')) {
      return formatDate(String(value));
    }
    return String(value);
  };

  const heroMeta = profileType === 'drivers' ? [
    { icon: <PhoneOutlinedIcon sx={{ fontSize: 18 }} />, label: 'Phone', value: String(record.mobile || '') },
    { icon: <EmailOutlinedIcon sx={{ fontSize: 18 }} />, label: 'Email', value: String(record.email || '') },
    { icon: <HomeOutlinedIcon sx={{ fontSize: 18 }} />, label: 'Address', value: String(record.address || '') },
    { icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />, label: 'Joined', value: record.joiningDate ? formatDate(String(record.joiningDate)) : '' },
  ] : profileType === 'vehicles' ? [
    { icon: <DirectionsCarIcon sx={{ fontSize: 18 }} />, label: 'Type', value: masterVehicleName(record, ['vehicleType', 'typeName']) },
    { icon: <LocalGasStationOutlinedIcon sx={{ fontSize: 18 }} />, label: 'Fuel', value: String(record.fuelType || '') },
    { icon: <SpeedOutlinedIcon sx={{ fontSize: 18 }} />, label: 'Max Speed', value: record.maxSpeed ? `${record.maxSpeed} km/h` : '' },
    {
      icon: <StorefrontOutlinedIcon sx={{ fontSize: 18 }} />,
      label: 'Ownership',
      value: isLeased(record) ? `Vendor · ${String(record.vendorName || 'Leased')}` : 'Owned (Company)',
    },
  ] : [
    { icon: <PhoneOutlinedIcon sx={{ fontSize: 18 }} />, label: 'Phone', value: String(record.mobile || '') },
    { icon: <HomeOutlinedIcon sx={{ fontSize: 18 }} />, label: 'Address', value: String(record.address || '') },
  ];

  const sidePanelTitle = profileType === 'drivers' ? 'License Status' : profileType === 'vehicles' ? 'Fleet Status' : 'Status';
  const licenseValid = profileType === 'drivers' && record.licenseExpiry
    ? new Date(String(record.licenseExpiry)) >= new Date()
    : null;

  const handlePrint = () => window.print();

  return (
    <Box sx={{ pb: 3 }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        mb: 2,
        flexWrap: 'wrap',
      }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(config.listPath)}
          sx={{ textTransform: 'none', fontWeight: 700, color: brandColors.textMuted }}
        >
          Back to {config.listLabel}
        </Button>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            onClick={() => navigate(config.listPath)}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS}px`, borderColor: brandColors.border }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadOutlinedIcon />}
            onClick={handlePrint}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: `${RADIUS}px`,
              background: brandCss.gradientPrimary,
              boxShadow: brandCss.shadowButton,
            }}
          >
            Download Profile
          </Button>
        </Box>
      </Box>

      {/* Hero card */}
      <Paper elevation={0} sx={{
        mb: 2.5,
        borderRadius: `${RADIUS + 4}px`,
        border: `1px solid ${brandColors.border}`,
        overflow: 'hidden',
        bgcolor: '#fff',
      }}>
        <Box sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2.5, sm: 3 },
          background: `linear-gradient(135deg, ${brandCss.primary}14 0%, #fff 55%)`,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.2fr 1.4fr 0.9fr' },
          gap: 2.5,
          alignItems: 'center',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            <Box sx={{
              width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
              bgcolor: brandCss.primary, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden',
              boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
            }}>
              {photo ? (
                <Box component="img" src={photo} alt={title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : config.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Chip label={status.label} size="small" sx={{ bgcolor: status.bg, color: status.color, fontWeight: 700, height: 24 }} />
                <Typography sx={{ fontSize: 12, color: brandColors.textMuted, fontWeight: 600 }}>
                  {config.entityLabel}
                </Typography>
              </Box>
              <Typography fontWeight={800} fontSize={{ xs: 22, sm: 26 }} sx={{ lineHeight: 1.2, wordBreak: 'break-word' }}>
                {title}
              </Typography>
              {subtitle ? (
                <Typography sx={{ mt: 0.35, fontSize: BASE_FONT_SIZE, color: brandColors.textMuted, fontWeight: 600 }}>
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Grid container spacing={1.75}>
            {heroMeta.map((item) => (
              <Grid item xs={12} sm={6} key={item.label}>
                <MetaRow icon={item.icon} label={item.label} value={item.value} />
              </Grid>
            ))}
          </Grid>

          <Paper elevation={0} sx={{
            p: 2,
            borderRadius: `${RADIUS}px`,
            border: `1px solid ${brandColors.border}`,
            bgcolor: '#fff',
            height: '100%',
          }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: brandColors.textMuted, mb: 1 }}>
              {sidePanelTitle}
            </Typography>
            {profileType === 'drivers' ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                  <CheckCircleOutlineIcon sx={{ color: licenseValid === false ? '#dc2626' : '#16a34a', fontSize: 22 }} />
                  <Typography fontWeight={800} color={licenseValid === false ? 'error.main' : 'success.main'}>
                    {licenseValid === false ? 'Expired' : 'Valid'}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: brandColors.textMuted }}>
                  Expires {record.licenseExpiry ? formatDate(String(record.licenseExpiry)) : '—'}
                </Typography>
              </>
            ) : (
              <>
                <Chip label={status.label} size="small" sx={{ bgcolor: status.bg, color: status.color, fontWeight: 700, mb: 1 }} />
                <Typography sx={{ fontSize: 13, color: brandColors.textMuted }}>
                  {profileType === 'vehicles'
                    ? (isLeased(record) ? `Leased from ${String(record.vendorName || 'vendor')}` : 'Company owned fleet')
                    : config.entityLabel}
                </Typography>
              </>
            )}
          </Paper>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 2,
            borderTop: `1px solid ${brandColors.border}`,
            bgcolor: '#fff',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: BASE_FONT_SIZE, minHeight: 48 },
            '& .Mui-selected': { color: brandCss.primaryDark },
            '& .MuiTabs-indicator': { backgroundColor: brandCss.primary, height: 3, borderRadius: 2 },
          }}
        >
          {config.tabs.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      </Paper>

      {tab === 0 && (
        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={8}>
            <Paper elevation={0} sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: `${RADIUS + 2}px`,
              border: `1px solid ${brandColors.border}`,
              bgcolor: '#fff',
            }}>
              {groups.map((group) => (
                <Box key={group.title} sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
                    {group.title === 'General Information' && profileType === 'vehicles' ? (
                      <Box sx={{
                        width: 32, height: 32, borderRadius: '10px',
                        bgcolor: `${brandCss.primary}18`, color: brandCss.primary,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <DirectionsCarIcon sx={{ fontSize: 18 }} />
                      </Box>
                    ) : group.title === 'Driver Information' ? (
                      <Box sx={{
                        width: 32, height: 32, borderRadius: '10px',
                        bgcolor: `${brandCss.primary}18`, color: brandCss.primary,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <PersonIcon sx={{ fontSize: 18 }} />
                      </Box>
                    ) : null}
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: brandColors.textDark }}>
                      {group.title}
                    </Typography>
                  </Box>
                  <Grid container spacing={1.5}>
                    {group.fields.map((f) => {
                      if (f.key === 'status' && (profileType === 'drivers' || profileType === 'vehicles')) {
                        return (
                          <FieldCard key={f.key} label={f.label} fullWidth={f.fullWidth}>
                            <FormControlLabel
                              control={<Switch checked={isActive} disabled size="small" color="success" />}
                              label={isActive ? 'Active' : (String(record.status || 'Inactive'))}
                              sx={{
                                m: 0,
                                '& .MuiFormControlLabel-label': {
                                  fontSize: BASE_FONT_SIZE,
                                  fontWeight: 700,
                                  textTransform: 'capitalize',
                                  color: isActive ? '#166534' : brandColors.textDark,
                                },
                              }}
                            />
                          </FieldCard>
                        );
                      }
                      return (
                        <FieldCard key={f.key} label={f.label} fullWidth={f.fullWidth}>
                          {formatValue(f, record[f.key])}
                        </FieldCard>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </Paper>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: `${RADIUS + 2}px`, border: `1px solid ${brandColors.border}`, bgcolor: '#fff' }}>
                <Typography sx={{ fontSize: BASE_FONT_SIZE, fontWeight: 800, mb: 1.5 }}>Quick Stats</Typography>
                <Grid container spacing={1.25}>
                  {(profileType === 'drivers' ? [
                    { label: 'Total Trips', value: '—' },
                    { label: 'Completed', value: '—' },
                    { label: 'Pending', value: '—' },
                    { label: 'Cancelled', value: '—' },
                  ] : profileType === 'vehicles' ? [
                    { label: 'Ownership', value: isLeased(record) ? 'Leased' : 'Owned' },
                    { label: 'Fuel', value: String(record.fuelType || '—') },
                    { label: 'Docs', value: String(record.documentStatus || '—') },
                    { label: 'Status', value: status.label },
                  ] : [
                    { label: 'Status', value: status.label },
                    { label: 'Client', value: String(record.clientName || '—') },
                  ]).map((s) => (
                    <Grid item xs={6} key={s.label}>
                      <Box sx={{ p: 1.25, borderRadius: `${RADIUS}px`, bgcolor: '#f8fafc', border: `1px solid ${brandColors.border}` }}>
                        <Typography sx={{ fontSize: 11, color: brandColors.textMuted, fontWeight: 600 }}>{s.label}</Typography>
                        <Typography sx={{ fontSize: BASE_FONT_SIZE, fontWeight: 800, textTransform: 'capitalize' }}>{s.value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              <Paper elevation={0} sx={{ p: 2, borderRadius: `${RADIUS + 2}px`, border: `1px solid ${brandColors.border}`, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
                  <Typography sx={{ fontSize: BASE_FONT_SIZE, fontWeight: 800 }}>Documents</Typography>
                  <Button size="small" onClick={() => setTab(1)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                    View All
                  </Button>
                </Box>
                <Typography sx={{ fontSize: 13, color: brandColors.textMuted }}>
                  Open the Documents tab to manage files and expiry dates.
                </Typography>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <Paper elevation={0} sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: `${RADIUS + 2}px`,
          border: `1px solid ${brandColors.border}`,
          bgcolor: '#fff',
        }}>
          {profileType === 'vehicles' ? (
            <VehicleDocumentChecklist
              vehicleId={id}
              vehicleNumber={String(record[config.titleField] || '')}
            />
          ) : (
            <DocumentPanel
              entityType={config.entityType}
              entityId={id}
              entityLabel={String(record[config.titleField] || '')}
              title="Attached Documents"
              compact
            />
          )}
        </Paper>
      )}
    </Box>
  );
};
